import useSWR from 'swr';

import { fetchEndpointData } from '@/utils/fetch.client';

// ── Types ────────────────────────────────────────────────────────────

/** Namespace of the built-in Zooid connector. Synthesised server-side for every space. */
export const ZOOID_SLUG = 'zooid';

/**
 * Namespace of the built-in repository connector: pull requests and their CI signals for the
 * repo a session is already working in. Also synthesised server-side, authenticated with the
 * GitHub account the user signed in with — there is nothing to configure, so it never appears
 * in the connector list.
 */
export const REPO_SLUG = 'repo';

/** Most tools a single connector may expose — mirrors `MAX_ENABLED_TOOLS` on the backend. */
export const MAX_ENABLED_TOOLS = 40;

/**
 * A configured connector.
 *
 * Credential *values* are never returned by the API — only `auth_header_names`, so the UI can
 * show which headers are set while treating their values as write-only.
 */
export interface Connector {
  id: string;
  team_id: string;
  created_by: string;
  slug: string;
  name: string;
  endpoint: string | null;
  auth_header_names: string[];
  enabled_tools: string[];
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/** One tool a server advertises, as offered for selection. */
export interface DiscoveredTool {
  name: string;
  description: string | null;
  /** Read tools run unprompted; writes stop for the user to confirm them. */
  read_only: boolean;
}

export interface CreateConnectorInput {
  slug: string;
  name: string;
  endpoint?: string | null;
  /** Omit to leave stored credentials alone; `{}` clears them. */
  auth_headers?: Record<string, string>;
  enabled_tools: string[];
  enabled: boolean;
}

export interface UpdateConnectorInput {
  name: string;
  endpoint?: string | null;
  auth_headers?: Record<string, string>;
  enabled_tools: string[];
  enabled: boolean;
}

interface ListConnectorsResponse {
  connectors: Connector[];
}

interface ConnectorResponse {
  connector: Connector;
}

interface DiscoverResponse {
  tools: DiscoveredTool[];
}

// ── SWR Hooks ──────────────────────────────────────────────────────────

/** List a space's connectors. Pass `null` to skip fetching (e.g. no team yet). */
export function useConnectors(teamId: string | null) {
  const { data, mutate, isLoading } = useSWR(teamId ? `/api/v1/teams/${teamId}/connectors` : null, () =>
    fetchEndpointData<ListConnectorsResponse>(`/api/v1/teams/${teamId}/connectors`),
  );
  return { connectors: data?.connectors ?? [], mutate, isLoading };
}

// ── Action Functions ───────────────────────────────────────────────────

export async function createConnector(teamId: string, body: CreateConnectorInput): Promise<Connector> {
  const res = await fetchEndpointData<ConnectorResponse>(`/api/v1/teams/${teamId}/connectors`, {
    method: 'POST',
    body,
  });
  return res.connector;
}

export async function updateConnector(
  teamId: string,
  connectorId: string,
  body: UpdateConnectorInput,
): Promise<Connector> {
  const res = await fetchEndpointData<ConnectorResponse>(`/api/v1/teams/${teamId}/connectors/${connectorId}`, {
    method: 'PUT',
    body,
  });
  return res.connector;
}

export async function deleteConnector(teamId: string, connectorId: string) {
  return fetchEndpointData(`/api/v1/teams/${teamId}/connectors/${connectorId}`, { method: 'DELETE' });
}

/** Ask a saved connector's server what tools it currently advertises. */
export async function discoverConnectorTools(teamId: string, connectorId: string): Promise<DiscoveredTool[]> {
  const res = await fetchEndpointData<DiscoverResponse>(`/api/v1/teams/${teamId}/connectors/${connectorId}/discover`, {
    method: 'POST',
  });
  return res.tools;
}

/**
 * Discover against an unsaved configuration, so the editor can populate its tool picker
 * before the first save. Nothing is persisted.
 */
export async function discoverDraftConnectorTools(
  teamId: string,
  body: CreateConnectorInput,
): Promise<DiscoveredTool[]> {
  const res = await fetchEndpointData<DiscoverResponse>(`/api/v1/teams/${teamId}/connectors/discover`, {
    method: 'POST',
    body,
  });
  return res.tools;
}
